"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useEffect, useState } from "react";
import { type CarouselApi } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

export interface HeroSlide {
    title: string;
    subtitle?: string;
    imageUrl: string;
    buttonText: string;
    flexDirection?: "start" | "end";
    tags?: string[];
}

interface HeroCarouselProps {
    slides: HeroSlide[];
}

const FALLBACK_SLIDES: HeroSlide[] = [
    {
        title: "L'art des épices authentiques",
        subtitle: "Des saveurs soigneusement sélectionnées pour sublimer chaque plat, inspirées des traditions du monde.",
        imageUrl: "/assets/landing/Hero-Section2.webp",
        buttonText: "Découvrir nos produits",
        flexDirection: "end",
    }
]

export default function HeroSection({ slides }: HeroCarouselProps) {
    const [api, setApi] = useState<CarouselApi>();
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        if (!api) return;

        const onSelect = () => {
            setCurrent(api.selectedScrollSnap());
        };

        api.on("select", onSelect);
        return () => {
            api.off("select", onSelect);
        };
    }, [api]);

    const displaySlides = slides.length > 0 ? slides : FALLBACK_SLIDES;

    return (
        <section className="relative w-full h-[85vh] md:h-175 overflow-hidden">
            <Carousel
                setApi={setApi}
                opts={{
                    loop: true,
                    align: "start",
                }}
                plugins={[
                    Autoplay({
                        delay: 5000,
                        stopOnInteraction: false,
                    }),
                ]}
                className="w-full h-full"
            >
                <CarouselContent className="h-full ml-0">
                    {displaySlides.map((slide, index) => {
                        const isEnd = slide.flexDirection === "end";
                        return (
                            <CarouselItem key={index} className="relative w-full h-full pl-0">
                                {/* Background Image */}
                                <Image
                                    src={slide.imageUrl}
                                    alt={slide.title}
                                    fill
                                    sizes="100vw"
                                    priority={index === 0}
                                    quality={70}
                                    className="object-cover"
                                    fetchPriority={index === 0 ? "high" : "low"}
                                />

                                {/* Overlays */}
                                <div className="absolute inset-0 bg-black/50 md:bg-transparent" />
                                <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/30 to-transparent md:hidden" />

                                {/* Content */}
                                <div
                                    className={cn(
                                        "relative z-10 h-full flex items-center justify-center md:items-center",
                                        isEnd ? "md:justify-end" : "md:justify-start"
                                    )}
                                >
                                    <div
                                        className={cn(
                                            "w-full max-w-sm mx-auto px-6 text-center flex flex-col items-center",
                                            "md:max-w-180 md:mx-0 md:px-0",
                                            isEnd ? "md:text-right md:pr-24 md:items-end" : "md:text-left md:pl-24 md:items-start"
                                        )}
                                    >
                                        {/* Title */}
                                        <h1
                                            className={cn(
                                                "font-brastika text-3xl sm:text-4xl text-white mb-10 md:text-6xl md:mb-6 transition-all duration-700 delay-100",
                                                current === index ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
                                                isEnd ? "md:text-primary-900 shadow-white" : "md:text-gold-500"
                                            )}
                                        >
                                            {slide.title}
                                        </h1>

                                        {/* Subtitle */}
                                        {slide.subtitle && (
                                            <p
                                                className={cn(
                                                    "text-sm text-white/80 leading-relaxed mb-10 max-w-xs md:text-lg md:mb-10 md:max-w-none transition-all duration-700 delay-300",
                                                    current === index ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
                                                    isEnd ? "md:text-primary-800" : "md:text-white"
                                                )}
                                            >
                                                {slide.subtitle}
                                            </p>
                                        )}

                                        {/* Tags */}
                                        {slide.tags && (
                                            <div
                                                className={cn(
                                                    "flex flex-wrap gap-2 mb-6 justify-center transition-all duration-700 delay-500",
                                                    current === index ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
                                                    isEnd ? "md:justify-end" : "md:justify-start"
                                                )}
                                            >
                                                {slide.tags.map((tag, tIndex) => (
                                                    <span
                                                        key={tIndex}
                                                        className="px-4 py-2 rounded-md h-10 text-sm font-medium bg-white/20 text-white backdrop-blur-sm md:bg-primary-100 md:text-primary-900"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* CTA Button */}
                                        <div className={cn(
                                            "transition-all duration-700 delay-700",
                                            current === index ? "scale-100 opacity-100" : "scale-90 opacity-0"
                                        )}>
                                            <Button size="lg" variant="default" className="shadow-xl">
                                                {slide.buttonText}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CarouselItem>
                        );
                    })}
                </CarouselContent>

                {/* Indicators */}
                <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center gap-2">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => api?.scrollTo(i)}
                            className={cn(
                                "h-1.5 rounded-full transition-all duration-300",
                                current === i ? "w-8 bg-white md:bg-primary-600" : "w-2 bg-white/40 md:bg-slate-300 hover:bg-white/60"
                            )}
                            aria-label={`Slide ${i + 1}`}
                        />
                    ))}
                </div>
            </Carousel>
        </section>
    );
}